import classNames from 'classnames';
import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useMeasureFloatingChromeObstruction } from 'features/layout';
import { modalVisibleSelector } from 'features/one_off_modal';
import { isTruthyRailExpanded, useGetRightRailWidth, useRightRail } from 'features/right_rail';
import { DEFAULT_SIDE_PANEL_WIDTH_PX, RESIZE_HANDLE_WIDTH_PX, SHELL_SIDE_PANEL_INSET_VAR, SHELL_SIDE_PANEL_WIDTH_VAR } from 'shared/shell_layout/constants';
import { MobileSidePanelPortal } from './MobileSidePanelPortal';
import { useShellBodyResize } from './shellBodyResize/ShellBodyResizeContext';
import { SidePanelContent } from './SidePanelContent';
import { useShellSidePanelFullScreenOverlay } from './useShellSidePanelFullScreenOverlay';
import { useSidePanel } from './useSidePanel';
import { notifySettingsModalRailLayout } from '../hooks/settings_modal_rail_layout_bus';
import { setSettingsModalElevatedSidePanelElement } from '../hooks/settings_modal_side_panel_focus_allow';
import { useSettingsModalRightRailElevation } from '../hooks/use_settings_modal_right_rail_elevation';
const PANEL_WIDTH = `${DEFAULT_SIDE_PANEL_WIDTH_PX}px`;
const sidePanelNoTransitionCss = "sdqzgk1";
const sidePanelOverlayCss = "s4f6n19";
const sidePanelClosedCss = "stllg07";
const sidePanelSettingsModalElevatedCss = "slm3tzz";
const sidePanelContainerCss = "sw92uoh";
const sidePanelInnerCss = "sye0cua";
interface SidePanelProps {
  /** Applied to the desktop container only; ignored on mobile (full-screen overlay has no border-radius). */
  desktopClassName?: string;
}

/**
 * Third-column panel in the app shell body row.
 * Animates in/out based on whether any side content is active (right rail, shareable, sidebar, user guide).
 * Visibility is driven by `useSidePanel`.
 *
 * **Desktop**: sits alongside `CenterPanel` in the body row; collapses to zero-width when closed.
 * **Full-screen overlay** (≤895px on most routes; ≤1119px on Notetaker recap routes, where side-by-side
 *   side panel would squeeze the video layout): delegates to `MobileSidePanelPortal`, which renders a
 *   full-screen fixed overlay via a React portal into `document.body`, bypassing ancestor stacking contexts.
 *
 * @param desktopClassName - Additional class applied to the desktop container only (e.g. shared `bodyPanelCss` border-radius).
 */
export const SidePanel = ({
  desktopClassName
}: SidePanelProps) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const {
    isOpen,
    close
  } = useSidePanel();
  const {
    isOpen: isRightRailOpen,
    current: rightRailCurrent
  } = useRightRail();
  const rightRailWidth = useGetRightRailWidth();
  const railExpanded = isTruthyRailExpanded(rightRailCurrent?.paneProps?.railExpanded);
  const {
    enabled: resizeEnabled,
    isDragging,
    sidePanelOverlay,
    sidePanelWidthPx
  } = useShellBodyResize();
  const useFullScreenOverlay = useShellSidePanelFullScreenOverlay();
  const isOneOffModalVisible = useSelector(modalVisibleSelector);
  const isSettingsModalRightRailElevation = useSettingsModalRightRailElevation();
  const shouldElevateForSettingsModal = isOpen && isSettingsModalRightRailElevation;
  useMeasureFloatingChromeObstruction('app-shell-side-panel', panelRef, isOpen && !useFullScreenOverlay && !isOneOffModalVisible);
  const setPanelRef = useCallback((node: HTMLDivElement | null) => {
    panelRef.current = node;
    setSettingsModalElevatedSidePanelElement(shouldElevateForSettingsModal ? node : null);
  }, [shouldElevateForSettingsModal]);
  const presetPanelWidth = useMemo(() => {
    if (isRightRailOpen && rightRailWidth) {
      return rightRailWidth;
    }
    return PANEL_WIDTH;
  }, [isRightRailOpen, rightRailWidth]);
  const panelWidth = useMemo(() => {
    if (!resizeEnabled) {
      return presetPanelWidth;
    }
    if (railExpanded) {
      // Overlay width from resize context; fallback to legacy calc() preset until body is measured.
      if (sidePanelOverlay) {
        return `${sidePanelWidthPx}px`;
      }
      return presetPanelWidth;
    }
    return `${sidePanelWidthPx}px`;
  }, [presetPanelWidth, railExpanded, resizeEnabled, sidePanelOverlay, sidePanelWidthPx]);
  const panelInset = useMemo(() => {
    if (!resizeEnabled) {
      return presetPanelWidth;
    }
    if (railExpanded && !sidePanelOverlay) {
      return presetPanelWidth;
    }
    return `${sidePanelWidthPx + RESIZE_HANDLE_WIDTH_PX}px`;
  }, [presetPanelWidth, railExpanded, resizeEnabled, sidePanelOverlay, sidePanelWidthPx]);
  useLayoutEffect(() => {
    panelRef.current?.style.setProperty(SHELL_SIDE_PANEL_WIDTH_VAR, panelWidth);
    const root = document.documentElement;
    root.style.setProperty(SHELL_SIDE_PANEL_WIDTH_VAR, panelWidth);
    root.style.setProperty(SHELL_SIDE_PANEL_INSET_VAR, panelInset);
    if (shouldElevateForSettingsModal) {
      notifySettingsModalRailLayout();
    }
    return () => {
      root.style.removeProperty(SHELL_SIDE_PANEL_WIDTH_VAR);
      root.style.removeProperty(SHELL_SIDE_PANEL_INSET_VAR);
    };
  }, [panelInset, panelWidth, shouldElevateForSettingsModal]);
  useLayoutEffect(() => {
    setSettingsModalElevatedSidePanelElement(shouldElevateForSettingsModal ? panelRef.current : null);
  }, [shouldElevateForSettingsModal]);
  if (useFullScreenOverlay) {
    return <MobileSidePanelPortal isOpen={isOpen} onClose={close} />;
  }
  return <div ref={setPanelRef} className={classNames(desktopClassName, sidePanelContainerCss, {
    [sidePanelClosedCss]: !isOpen,
    [sidePanelSettingsModalElevatedCss]: shouldElevateForSettingsModal,
    [sidePanelOverlayCss]: resizeEnabled && sidePanelOverlay,
    [sidePanelNoTransitionCss]: resizeEnabled && isDragging
  })} id="shell-side-panel" data-shell-side-panel-overlay={sidePanelOverlay ? '' : undefined} aria-hidden={!isOpen || undefined}>
      <div className={sidePanelInnerCss}>
        {isOpen && <SidePanelContent />}
      </div>
    </div>;
};

require("./SidePanel.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./SidePanel.tsx");